/**
 * The type picker of one inbox row (PROD-3339, behind `?x=intake-type`). Starts on the stored type or the classifier's
 * suggestion (marked "Suggested"); Confirm type saves the pick through the existing inbox Add (source User). A lab report
 * pick then reads the values: the Add answers review_required and the run is retried (no values yet) or review opens
 * (values already read). Nothing is read before the click. Signal DS Select and Button only.
 */
import { useRef, useState } from 'react';
import { Button, Select } from '@scripsteam/scrips-signal-ds';
import { IntakeApiError } from '../api/intake-client';
import { useAddInboxDocument, useRetryInboxRun } from '../hooks/use-intake-inbox';
import { DOC_TYPE_LABEL, DOC_TYPE_ORDER, confirmTypeAction, labPickNeedsReading, type TypePickerView } from '../lib/inbox-view';
import type { InboxDocument, IntakeDocType } from '../types';

const TYPE_OPTIONS = DOC_TYPE_ORDER.map((value) => ({ value, label: DOC_TYPE_LABEL[value] }));

export interface InboxTypePickerProps {
  doc: InboxDocument;
  patientId: string;
  view: TypePickerView;
  /** Opens the review page (a lab report whose values are already read). */
  onReview: () => void;
}

const isReviewRequired = (e: unknown) => e instanceof IntakeApiError && e.status === 409 && e.code === 'review_required';

export function InboxTypePicker({ doc, patientId, view, onReview }: InboxTypePickerProps) {
  // The user's own unsaved pick (undefined until they choose). Until then the picker follows the row as the inbox refetches it
  // (a new suggestion, a stored type); once they chose, a refetch never overwrites their pick. No remount: the save's own
  // error and pending state stay on the row while the inbox refreshes.
  const [userPick, setUserPick] = useState<IntakeDocType | undefined>(undefined);
  const picked: IntakeDocType | null = userPick === undefined ? view.initial : userPick;
  // `saved`: the server took the pick (the Add succeeded, or a lab pick answered review_required). The local pick then stays
  // shown until the next refetch that changes the row's type, whatever that type is (the pick, or someone else's change), and is
  // dropped there, so the picker follows the row again. Not on the save itself: until the refetch arrives the old type would flash
  // back. A refetch that already carries the pick drops it too. Adjusted while rendering (React's pattern), no effect.
  const [saved, setSaved] = useState(false);
  const [seenDocType, setSeenDocType] = useState(doc.docType);
  if (doc.docType !== seenDocType) {
    setSeenDocType(doc.docType);
    if (userPick !== undefined && (saved || userPick === doc.docType)) {
      setUserPick(undefined);
      setSaved(false);
    }
  }
  // What the last Confirm type did: a review_required answer is the expected step of a lab pick only.
  const [submitted, setSubmitted] = useState<'add' | 'read' | null>(null);
  const add = useAddInboxDocument(patientId);
  const retry = useRetryInboxRun(patientId);
  const action = confirmTypeAction(doc, picked);
  const failed = (add.isError && !(submitted === 'read' && isReviewRequired(add.error))) || retry.isError;

  // Set before the save starts and released when the last request of this Confirm settles: two quick clicks (before React
  // re-renders the disabled button) still send one Add.
  const inFlight = useRef(false);
  const release = () => {
    inFlight.current = false;
  };

  const confirmType = () => {
    if (inFlight.current || picked === null || action === 'none') return;
    inFlight.current = true;
    setSubmitted(action);
    retry.reset();
    add.mutate(
      { documentId: doc.documentId, docType: picked },
      {
        onSuccess: () => {
          setSaved(true);
          release();
        },
        onError: (e) => {
          if (!(isReviewRequired(e) && action === 'read')) {
            release();
            return;
          }
          setSaved(true); // the lab pick is stored; review or the re-read takes it from here
          if (labPickNeedsReading(doc) && doc.runId) retry.mutate({ runId: doc.runId }, { onSettled: release });
          else {
            release();
            onReview();
          }
        },
      },
    );
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-2">
        {/* Locked while the save or the re-read runs: a pick made then would be marked saved by the late answer. */}
        <Select<string>
          disabled={add.isPending || retry.isPending}
          aria-label={`Type of ${doc.fileName}`}
          size="compact"
          options={TYPE_OPTIONS}
          placeholder="Pick a type…"
          // Always controlled: the DS Select fixes controlled/uncontrolled at mount (an undefined value would never update). '' = no type yet.
          value={picked ?? ''}
          onChange={(v) => {
            setSaved(false); // a new choice is unsaved again: a refetch must not drop it
            setUserPick(v as IntakeDocType);
          }}
        />
        <Button size="sm" variant="secondary" loading={add.isPending || retry.isPending} disabled={add.isPending || retry.isPending || action === 'none'} onClick={confirmType}>
          Confirm type
        </Button>
      </div>
      {view.suggested !== null && picked === view.suggested ? <span className="text-xs text-text-secondary">Suggested</span> : null}
      {failed ? (
        <span role="alert" className="text-xs text-status-error">
          That did not work. Try again.
        </span>
      ) : null}
    </div>
  );
}
